import { useEffect, useRef, RefObject } from 'react';

/**
 * Hook to clone a DOM element into a container ref.
 *
 * @param sourceElement - The element to clone, or null/undefined to clear the container
 * @returns Object with `ref` to attach to the container element
 */
export const useElementClone = <T extends HTMLElement = HTMLDivElement>(sourceElement: HTMLElement | null | undefined): { ref: RefObject<T> } => {
  const ref = useRef<T>(null);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    const container = ref.current;

    // Clear container immediately if no source element
    if (!sourceElement) {
      if (container) {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      }
      return;
    }

    // Cancel any pending RAF callback
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const rafId = requestAnimationFrame(() => {
      rafIdRef.current = null;
      const currentContainer = ref.current;
      if (!currentContainer) {
        return;
      }

      // Clear existing content before appending clone
      while (currentContainer.firstChild) {
        currentContainer.removeChild(currentContainer.firstChild);
      }

      const clone = sourceElement.cloneNode(true) as HTMLElement;
      currentContainer.appendChild(clone);
    });

    rafIdRef.current = rafId;

    return () => {
      // Cancel pending RAF if cleanup happens before callback executes
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      // Clear container on cleanup
      if (container) {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      }
    };
  }, [sourceElement]);

  return { ref };
};
